import { useEffect, useState } from 'react';
import { logEvent } from '@amplitude/analytics-browser';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { useMediaQuery, useTheme } from '@mui/material';
import Modal from '../../componentsv2/Modal';
import ProductEmptyState from '../../componentsv2/ProductEmptyState';
import Layout from '../../componentsv2/Layout';
import Button from '../../componentsv2/Button';
import Typography from '../../componentsv2/Typography';
import PageHeader from '../../componentsv2/PageHeader';
import Detail from '../../componentsv2/Detail';
import StatusTracker from '../../componentsv2/StatusTrackerv2';
import CopyToClipboardButton from '../../componentsv2/CopyToClipboardButton';
import Chip from '../../componentsv2/Chip';
import {
  MessageSentIcon,
  MiniNotepadIcon,
  MiniInboxIcon,
  StarIcon,
  MiniContractIcon,
  NoteIcon,
} from '../../components/Icons';
import SimpleBackdrop from '../../components/Backdrop';
import Tooltip from '../../componentsv2/Tooltip';
import {
  createLLCProductRequest,
  getLLC,
  getLLCProductRequest,
  setSignableDocumentInProcess,
} from '../../services/llc';
import { optOutRegisteredAgent } from '../../services/registeredAgent';
import ConfirmDialog from '../../componentsv2/ConfirmDialog';
import { useSnackbar } from '../../context/snackbar';
import { useAppSelector } from '../../store';
import { useUserLocation } from '../../hooks/useUserLocation';
import { useTranslation } from '../../hooks/i18n';
import { formatDate } from '../../utils/common';
import { LLCEvents } from '../../constants/events';
import { HELP_CENTER_URL } from '../../constants/common';
import { filesURL } from '../../constants/auth';
import { useProductLock } from '../../hooks/useProductLock';
import { baseURL } from '../../constants/api';
import { nullDate } from '../../constants/servicing';
import { Nullable } from '../../types';
import { UserRole } from '../../types/companyUser';
import { APILLC, LLCDocument, LLCDocumentType, LLCProductRequest, LLCStatus } from '../../types/llc';
import { RegisteredAgentRenewalAction } from '../../types/registeredAgent';
import { COLORS } from '../../themev2';
import classes from './USLLC.module.scss';

const BENEFITS = ['nameAndState', 'einFiling', 'globalAccountReady'];

const STEPS = ['requestFormation', 'readyToOperate', 'openBankAccount'];

const LLC_REQUEST_COOLDOWN_MS = 48 * 60 * 60 * 1000;

const statusColorsMap = {
  PENDING: { backgroundColor: '#FCF0DB', color: '#AC8318' },
  SIGNED: { backgroundColor: '#E1FAED', color: '#11705F' },
  IN_PROCESS: { backgroundColor: COLORS.gray[500], color: COLORS.gray[1200] },
};

function extractDocumentByType(
  documents: LLCDocument[],
  documentType: LLCDocument['documentType']
): LLCDocument | undefined {
  let extracted;

  const index = documents.findIndex((document) => document.documentType === documentType);
  if (index) {
    extracted = documents.splice(index, 1)[0];
  }
  return extracted;
}

function orderDocuments(documents: LLCDocument[]): LLCDocument[] {
  let result = [...documents];
  const einDocument = extractDocumentByType(result, LLCDocumentType.EIN_DOCUMENT);
  const ss4 = extractDocumentByType(result, LLCDocumentType.SS4);
  const w9form = extractDocumentByType(result, LLCDocumentType.FORM_W9);
  const _8821form = extractDocumentByType(result, LLCDocumentType.FORM_8821);
  return [
    ...(einDocument ? [einDocument] : []),
    ...(ss4 ? [ss4] : []),
    ...result,
    ...(_8821form ? [_8821form] : []),
    ...(w9form ? [w9form] : []),
  ];
}

function ensureExternalUrl(url?: Nullable<string>): string {
  if (!url) return '#';
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return `https://${url}`;
}

const USLLC = () => {
  const { t } = useTranslation(['usLLC']);
  const { isLocked, lockedHint } = useProductLock();
  const { language, locale } = useUserLocation();
  const selectedCompany = useAppSelector((state) => state.companyUser.companyUser?.selectedCompany);
  const isVisualizer = useAppSelector(
    (state) => state.companyUser.companyUser?.selectedCompanyRole === UserRole.VISUALIZER
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmittingLLC, setIsSubmittingLLC] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showEinCongratsModal, setShowEinCongratsModal] = useState(false);
  const [showRaOptOutDialog, setShowRaOptOutDialog] = useState(false);
  const [isOptingOutRa, setIsOptingOutRa] = useState(false);
  const { showSnackbar } = useSnackbar();
  const [llc, setLlc] = useState<Nullable<APILLC>>(null);
  const [llcProductRequest, setLlcProductRequest] = useState<Nullable<LLCProductRequest>>(null);
  const [orderedDocuments, setOrderedDocuments] = useState<LLCDocument[]>([]);
  const isEinReady = llc?.status === LLCStatus.EIN_ISSUED;
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (!selectedCompany) return;
    getProductRequest();
    getLLC(selectedCompany.id)
      .then((result) => {
        if (result.length > 0) {
          setLlc(result[0]);
        } else {
          setLlc(null);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [selectedCompany]);

  useEffect(() => {
    logEvent(LLCEvents.VIEW_PAGE, { Status: llc?.status });
  }, [llc?.status]);

  useEffect(() => {
    if (llc) {
      setOrderedDocuments(orderDocuments(llc.documents));
    }
  }, [llc]);

  useEffect(() => {
    if (isEinReady && !localStorage.getItem('einSuccessDismissed')) {
      setShowEinCongratsModal(true);
    }
  }, [isEinReady]);

  const hasRecentProductRequest = llcProductRequest?.createdAt
    ? Date.now() - new Date(llcProductRequest.createdAt).getTime() < LLC_REQUEST_COOLDOWN_MS
    : false;

  const handleDiscoverMore = () => {
    logEvent(LLCEvents.CLICK_DISCOVER_MORE);
    window.open(HELP_CENTER_URL, '_blank', 'noopener,noreferrer');
  };

  const handleSubmitForLLC = async () => {
    setIsSubmittingLLC(true);
    await createLLCProductRequest(selectedCompany?.id!)
      .then((result) => {
        logEvent(LLCEvents.SUBMIT_REQUEST);
        setShowConfirmationModal(true);
        setLlcProductRequest(result);
      })
      .finally(() => setIsSubmittingLLC(false));
  };

  const getProductRequest = async () => {
    setIsLoading(true);
    await getLLCProductRequest(selectedCompany?.id!)
      .then((result) => {
        if (result.length > 0) {
          setLlcProductRequest(result[0]);
        } else {
          setLlcProductRequest(null);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const closeCongratsModal = () => {
    setShowEinCongratsModal(false);
    localStorage.setItem('einSuccessDismissed', 'true');
  };

  const handleConfirmOptOutRegisteredAgent = async () => {
    if (!selectedCompany || !llc) return;
    setIsOptingOutRa(true);
    try {
      await optOutRegisteredAgent(selectedCompany.id, llc.id);
      logEvent(LLCEvents.REGISTERED_AGENT_OPTED_OUT);
      setLlc((prev) =>
        prev
          ? {
              ...prev,
              registeredAgentLastDecision: RegisteredAgentRenewalAction.OPTOUT,
              registeredAgentLastDecisionAt: new Date().toISOString(),
            }
          : prev
      );
      showSnackbar(t('usLLC:registeredAgent.optOut.success'), 'success');
      setShowRaOptOutDialog(false);
    } catch {
      showSnackbar(t('usLLC:registeredAgent.optOut.error'), 'error');
    } finally {
      setIsOptingOutRa(false);
    }
  };

  if (isLoading) return <SimpleBackdrop hasOpaqueBackground isLoading />;

  return (
    <Layout>
      <Grid container rowSpacing={2}>
        {!llc ? (
          <Grid item xs={12}>
            <ProductEmptyState
              title={t('title')}
              subtitle={t('emptyState.subtitle')}
              benefits={BENEFITS.map((key) => t(`emptyState.benefits.${key}`))}
              cta={
                isVisualizer
                  ? undefined
                  : {
                      label: t('emptyState.cta'),
                      onClick: handleSubmitForLLC,
                      isLoading: isSubmittingLLC,
                      disabled: hasRecentProductRequest,
                      isLocked,
                      tooltip: isLocked ? lockedHint : undefined,
                    }
              }
              stepsTitle={t('common:productEmptyState.howDoesItWork')}
              steps={STEPS.map((key) => ({
                title: t(`emptyState.steps.${key}.title`),
                description: t(`emptyState.steps.${key}.description`),
              }))}
              discoverMoreLabel={t('common:productEmptyState.discoverMore')}
              onDiscoverMore={handleDiscoverMore}
              heroSrc={`${filesURL}/${t('emptyState.heroImage')}`}
            />
          </Grid>
        ) : (
          <>
            <Grid item xs={12}>
              <PageHeader title={t('title')} subtitle={t('subtitle')} />
            </Grid>
            <Grid item xs={12}>
              <Box className={classes.boxWrapper}>
                <Grid container>
                  <Grid item xs={12}>
                    <StatusTracker
                      statuses={[
                        {
                          label: t('llcRequest.status.STATE_FILING.label'),
                          value: 'STATE_FILING',
                          icon: <MiniInboxIcon />,
                        },
                        {
                          label: t('llcRequest.status.COMPANY_CONFIRMATION.label'),
                          value: 'COMPANY_CONFIRMATION',
                          icon: <MiniNotepadIcon isCurrentColor />,
                        },
                        {
                          label: t('llcRequest.status.SS4_SIGN.label'),
                          value: 'SS4_SIGN',
                          icon: <MiniContractIcon />,
                        },
                        {
                          label: t('llcRequest.status.EIN_APPLICATION.label'),
                          value: 'EIN_APPLICATION',
                          icon: <NoteIcon />,
                        },
                      ]}
                      currentStatus={llc.status}
                      isComplete={isEinReady}
                      justifyContent="center"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <Typography fontWeight={600} sx={{ color: COLORS.cian[800] }}>
                      {t(`llcRequest.status.${llc.status}.header`)}
                    </Typography>
                    <Typography variant="h6" fontWeight={700}>
                      {t(`llcRequest.status.${llc.status}.title`, {
                        company: selectedCompany?.name,
                        place: selectedCompany?.addressState
                          ? t(`llcRequest.inTheStateOf`, {
                              state: selectedCompany?.addressState,
                            })
                          : '',
                      })}
                    </Typography>
                    <Typography fontWeight={600} sx={{ color: COLORS.gray[1200] }}>
                      {t(`llcRequest.status.${llc.status}.description`)}
                    </Typography>
                    {isEinReady && llc.taxId && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                          }}
                        >
                          <Typography sx={{ color: COLORS.gray[1200], fontWeight: 600 }}>
                            {llc?.taxIdType} {t('llcRequest.number')}:
                          </Typography>
                          <Typography style={{ color: COLORS.indigo[500] }}>{llc.taxId}</Typography>
                        </Box>
                        <CopyToClipboardButton
                          textToCopy={llc.taxId!}
                          onClick={() => {
                            logEvent(LLCEvents.CLICK_COPY_EIN);
                          }}
                        />
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </Box>
            </Grid>
            {llc.companyName && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    background: COLORS.gray[700],
                    borderRadius: '12px',
                    backgroundColor: '#FFFFFF',
                    padding: '1em 2em',
                  }}
                >
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: '18px',
                          lineHeight: '27px',
                          color: COLORS.brand.night,
                        }}
                      >
                        {llc.companyName}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ borderColor: '#DEE9FA' }} />
                    </Grid>
                    <Grid item xs={12}>
                      <Grid container spacing={1}>
                        <Grid item xs={6} md={3}>
                          <Detail
                            value={llc.status ? t(`llcRequest.status.${llc.status}.label`) : '-'}
                            title={t('details.status')}
                          />
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Detail
                            value={`${
                              selectedCompany?.formationDate
                                ? formatDate(
                                    new Date(selectedCompany?.formationDate),
                                    locale,
                                    {
                                      year: 'numeric',
                                      month: 'numeric',
                                      day: 'numeric',
                                    },
                                    false,
                                    true
                                  )
                                : llc.companyFormationDate
                                ? formatDate(
                                    new Date(llc.companyFormationDate),
                                    locale,
                                    {
                                      year: 'numeric',
                                      month: 'numeric',
                                      day: 'numeric',
                                    },
                                    false,
                                    true
                                  )
                                : nullDate
                            }`}
                            title={t('details.companyFormationDate')}
                            isCopyable
                          />
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Detail
                            value={`${
                              llc.addressStreet
                                ? `${llc?.addressStreet}, ${llc?.addressCity}, ${llc?.addressState}`
                                : '-'
                            }`}
                            title={t('details.address')}
                            isCopyable
                          />
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Detail value={llc.taxId || '-'} title={t('details.taxIdNumber')} isCopyable />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            )}
            {llc.documents.length > 0 &&
            llc.documents.some((document) => document.assets.length > 0 || !!document.signLink) ? (
              <Grid item xs={12}>
                <Typography variant="h6" fontWeight={700} sx={{ margin: '1em 0 2em' }}>
                  {t('llcRequest.documents.title')}
                </Typography>
                <Box className={classes.boxWrapper}>
                  {orderedDocuments.map((document) =>
                    // only show document with uploaded assets or signable ones
                    document.assets.length > 0 || !!document.signLink ? (
                      <Grid item xs={12}>
                        <Grid
                          container
                          justifyContent="center"
                          alignItems="center"
                          gap="1em"
                          sx={{ flex: 1 }}
                        >
                          <Grid item xs={12} md>
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px',
                                paddingLeft: isMdDown ? 0 : '2.5em',
                              }}
                            >
                              <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                {orderedDocuments.filter((doc) => doc.assets.length > 0 || !!doc.signLink)[0]
                                  ?.id === document.id && (
                                  <Typography
                                    sx={{
                                      padding: '4px 12px',
                                      backgroundColor: COLORS.cian[100],
                                      borderRadius: '8px',
                                      fontWeight: 600,
                                      color: COLORS.cian[600],
                                    }}
                                  >
                                    {t('llcRequest.documents.new')}
                                  </Typography>
                                )}
                                <Typography sx={{ fontSize: '1.2em', fontWeight: 'bold' }}>
                                  {t(`llcRequest.documents.${document.documentType}.title`)}
                                </Typography>
                              </Box>
                              <Typography>
                                {t(`llcRequest.documents.${document.documentType}.description`)}
                              </Typography>
                            </Box>
                          </Grid>
                          {document.isSignable && (
                            <Grid item xs={12} md={2}>
                              <Typography sx={{ display: 'flex', justifyContent: 'center' }}>
                                <Chip
                                  sx={{ ...statusColorsMap[document.status] }}
                                  label={t(`llcRequest.documentStatus.${document.status}`)}
                                />
                              </Typography>
                            </Grid>
                          )}
                          <Grid item xs={isMdDown ? 12 : undefined}>
                            {document.isSignable && document.status !== 'SIGNED' ? (
                              !isVisualizer ? (
                                <a
                                  href={ensureExternalUrl(document.signLink)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <Button
                                    onClick={() => {
                                      setSignableDocumentInProcess(
                                        selectedCompany?.id!,
                                        llc.id,
                                        document.id
                                      ).then((result) => {
                                        logEvent(LLCEvents.CLICK_SIGN_DOCUMENT, {
                                          Document: document.documentType,
                                        });
                                        setLlc((prev) => ({
                                          ...prev!,
                                          documents: prev!.documents.map((doc) =>
                                            doc.id === result.id ? result : doc
                                          ),
                                        }));
                                      });
                                    }}
                                  >
                                    {t('llcRequest.buttons.sign')}
                                  </Button>
                                </a>
                              ) : (
                                <Tooltip title={t('common:visualizerRole.actionNotAllowed')}>
                                  <Box>
                                    <Button disabled>{t('llcRequest.buttons.sign')}</Button>
                                  </Box>
                                </Tooltip>
                              )
                            ) : (
                              <a
                                href={
                                  document?.assets[0]?.uuid
                                    ? `${baseURL}/assets/${document?.assets[0]?.uuid}`
                                    : '#'
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <Button
                                  type="outline"
                                  onClick={() => {
                                    logEvent(LLCEvents.VIEW_DOCUMENT, { Document: document.documentType });
                                  }}
                                >
                                  {t('llcRequest.buttons.view')}
                                </Button>
                              </a>
                            )}
                          </Grid>
                        </Grid>
                        {orderedDocuments
                          .filter((doc) => doc.assets.length > 0 || !!doc.signLink)
                          .slice(-1)[0]?.id !== document.id && (
                          <Divider sx={{ borderColor: COLORS.gray[700], margin: '32px 0' }} />
                        )}
                      </Grid>
                    ) : null
                  )}
                </Box>
              </Grid>
            ) : null}
            {isEinReady &&
              llc.registeredAgentLastDecision !== RegisteredAgentRenewalAction.OPTOUT &&
              !isVisualizer && (
                <Grid item xs={12}>
                  <Box className={classes.boxWrapper}>
                    <Grid container rowSpacing={1.5}>
                      <Grid item xs={12}>
                        <Typography variant="h6" fontWeight={700}>
                          {t('usLLC:registeredAgent.section.title')}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography sx={{ color: COLORS.gray[1200] }}>
                          {t('usLLC:registeredAgent.section.description')}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
                        <Button type="outline" color="danger" onClick={() => setShowRaOptOutDialog(true)}>
                          {t('usLLC:registeredAgent.section.optOutButton')}
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              )}
            {llc.genericDocuments.map(({ titleEn, titleEs, descriptionEn, descriptionEs, assets }) => {
              return (
                <Grid item xs={12}>
                  <Box className={classes.boxWrapper}>
                    <Grid container justifyContent="center" alignItems="center" gap="1em" sx={{ flex: 1 }}>
                      <Grid item xs={12} md>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                            paddingLeft: isMdDown ? 0 : '2.5em',
                          }}
                        >
                          <Typography sx={{ fontSize: '1.2em', fontWeight: 'bold' }}>
                            {language === 'es' ? titleEs : titleEn}
                          </Typography>
                          <Typography>{language === 'es' ? descriptionEs : descriptionEn}</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={isMdDown ? 12 : undefined}>
                        <a
                          href={assets[0]?.uuid ? `${baseURL}/assets/${assets[0]?.uuid}` : '#'}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button type="outline" onClick={() => {}}>
                            {t('llcRequest.buttons.view')}
                          </Button>
                        </a>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              );
            })}
          </>
        )}
      </Grid>
      {showConfirmationModal && (
        <Modal
          isOpen={showConfirmationModal}
          handleClose={() => setShowConfirmationModal(false)}
          isAutoHeight
        >
          <Grid container rowSpacing={2}>
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
              <MessageSentIcon />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" textAlign="center" fontWeight={700}>
                {t('confirmationModal.thanks')}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography
                variant="body1"
                textAlign="center"
                sx={{ color: COLORS.gray[1100], paddingBottom: '1em' }}
              >
                {t('confirmationModal.message')}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Button fullWidth onClick={() => setShowConfirmationModal(false)} color="secondary">
                {t('confirmationModal.confirmButtonTitle')}
              </Button>
            </Grid>
          </Grid>
        </Modal>
      )}
      <ConfirmDialog
        open={showRaOptOutDialog}
        title={t('usLLC:registeredAgent.optOut.title')}
        content={t('usLLC:registeredAgent.optOut.content')}
        onCancel={() => setShowRaOptOutDialog(false)}
        onConfirm={handleConfirmOptOutRegisteredAgent}
        isLoading={isOptingOutRa}
      />
      {showEinCongratsModal && (
        <Modal
          isOpen={showEinCongratsModal}
          handleClose={() => closeCongratsModal()}
          sx={{ paddingX: '4em', minHeight: '450px' }}
        >
          <Grid container rowSpacing={2}>
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
              <StarIcon />
            </Grid>
            <Grid item xs={12}>
              <Typography
                variant="h4"
                textAlign="center"
                sx={{
                  fontSize: '32px',
                  background:
                    'linear-gradient(74.44deg, #0BAAA1 -10.53%, #2D95CD 46.22%, #2D96CD 46.23%, #2D96CC 46.24%, #2EC4B6 98.43%)',
                  backgroundClip: 'text',
                  '-webkit-background-clip': 'text',
                  color: 'transparent',
                }}
              >
                {t('llcRequest.ein.modal.title')}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography
                sx={{ whiteSpace: 'pre-wrap', fontSize: '20px' }}
                fontWeight={700}
                textAlign="center"
              >
                {t('llcRequest.ein.modal.subtitle')}
              </Typography>
            </Grid>
            <Grid item xs={12} sx={{ marginBottom: '2em' }}>
              <Typography textAlign="center" sx={{ color: COLORS.gray[1100] }}>
                {t('llcRequest.ein.modal.description')}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Button fullWidth onClick={() => closeCongratsModal()} color="secondary">
                {t('llcRequest.ein.modal.button')}
              </Button>
            </Grid>
          </Grid>
        </Modal>
      )}
    </Layout>
  );
};

export default USLLC;
